import { FieldRenderProps } from "@progress/kendo-react-form";
import { FC, FunctionComponent, ReactNode } from "react";
import { Label } from "@progress/kendo-react-labels";
import { DropDownTree, DropDownTreeChangeEvent, DropDownTreeExpandEvent } from "@progress/kendo-react-dropdowns";
import { TicketApi } from "../../../redux/api/TicketApi";
import React from "react";
import { expandedState, processTreeData } from "../../../utils/TreeDataOperations";
import { IQuickNote } from "../../../types/Ticket/IQuickNotes";
import { Button } from "@progress/kendo-react-buttons";
import AddQuickNote from "./AddQuickNote";
import { Api } from "../../../redux/api/BaseApi";
import './QuickNotes.css'
import * as svg from "@progress/kendo-svg-icons";
import { Popover } from "@progress/kendo-react-tooltip";
import { SvgIcon } from "@progress/kendo-react-common";
import { useCurrentUserContext } from "../../../context/UserContext";
const QuickNotesList: FunctionComponent<FieldRenderProps> = props => {
    const {
        quickNoteId,
        label,
        id,
        valid,
        disabled,
        optional,
    } = props;
    const selectField = "selected";
    const expandField = "expanded";
    const dataItemKey = "Id";
    const subItemsField = "QuickNotes";
    const { profile: userProfile } = useCurrentUserContext();
    const fields = { selectField, expandField, dataItemKey, subItemsField };
    const { data: quickNotes, refetch } = TicketApi.useGetQuickNotesQuery(undefined, { skip: !userProfile?.Features?.Quick_Notes });
    const selectedQuickNote = quickNotes?.map(q => q.QuickNotes.find(qn => qn.Id === quickNoteId)).find(qn => qn?.Id === quickNoteId);
    const [value, setValue] = React.useState<IQuickNote>(selectedQuickNote);
    const [expanded, setExpanded] = React.useState(quickNotes?.length > 0 ? [quickNotes?.[0][dataItemKey]] : []);
    const [open, setOpen] = React.useState<boolean>();
    const [popupVisible, setPopupVisible] = React.useState<boolean>();
    const { data: quickNote } = TicketApi.useGetQuickNoteAsActionQuery({ id: quickNoteId, ticketId: 0 }, { skip: !quickNoteId })

    //populate the popup text with the number of actions
    const actionCount = quickNoteId ? (quickNote?.Quick_Note_Priority_Name ? 1 : 0) + (quickNote?.Quick_Note_Status_Name ? 1 : 0) + (quickNote?.Quick_Note_Type_Name ? 1 : 0) : 0;
    const popupNote = quickNote && (<>
        <h6>Performing {actionCount} {actionCount === 1 ? 'action' : 'actions'}:</h6>
        <ul>
            {quickNote.Quick_Note_Status_Name && <li>Status: {quickNote.Quick_Note_Status_Name}</li>}
            {quickNote.Quick_Note_Priority_Name && <li>Priority: {quickNote.Quick_Note_Priority_Name}</li>}
            {quickNote.Quick_Note_Type_Name && <li>Type: {quickNote.Quick_Note_Type_Name}</li>}
        </ul>
    </>);

    const onChange = (event: DropDownTreeChangeEvent) => {

        //console.log('onChange', event, expanded.includes(event?.value?.Id))
        if (event?.value && event?.value.QuickNotes) {
            //console.log('cat');
            if (!expanded.includes(event?.value.Id))
                setExpanded([event?.value.Id]); //expand
            else
                //let newArray = [...expanded].splice(expanded.indexOf(event?.value.Id)
                setExpanded((oldVal) => {
                    let newVal = [...oldVal]
                    newVal.splice(expanded.indexOf(event?.value.Id), 1)
                    return newVal;
                }); //collaps
            setOpen(true); //stay open

            return
        }

        setValue(event.value);
        props.onChange(event);
        setOpen(undefined); //Close
    }
    const onExpandChange = React.useCallback(
        (event: DropDownTreeExpandEvent) =>
            setExpanded(expandedState(event.item, dataItemKey, expanded)),
        [expanded]
    );

    const treeData = React.useMemo(
        () => {
            return processTreeData(quickNotes || [], { expanded, value }, fields)
        },

        [quickNotes, expanded, value]
    );

    //After a quicknote has beem saved
    const [newId, setNewId] = React.useState<number>(null)
    const quickNoteSaved = (id: number) => {
        setPopupVisible(false);
        setNewId(id)
        refetch()
    }

    return (
        <>
            <Label className="d-flex align-items-center"
                editorId={id}
                editorValid={valid}
                editorDisabled={disabled}
                optional={optional}
            >
                {label} <QuickNotePopup actionCount={actionCount} popupText={popupNote} />
            </Label>
            <div className={"flex"}>
                <DropDownTree data={treeData}
                    onBlur={() => setOpen(undefined)}
                    opened={open}
                    placeholder="Select..."
                    value={selectedQuickNote}
                    dataItemKey="Id"
                    textField="Name"
                    subItemsField="QuickNotes"
                    onChange={onChange}
                    selectField={selectField}
                    expandField={expandField}
                    onExpandChange={onExpandChange}
                    popupSettings={{ width: 500, className: 'quick-note-dropdown' }}
                />
                {userProfile?.Features.Add_Quick_Note && <Button icon='add' title="Add Quick Note" onClick={() => setPopupVisible(true)} formNoValidate={true}></Button>}
                {popupVisible && <AddQuickNote onClose={() => setPopupVisible(false)} onSave={(id) => quickNoteSaved(id)} />}
            </div>
        </>
    )
}

export type QuickNotePopupProps = {
    actionCount: number;
    popupText: ReactNode;
}


export const QuickNotePopup: FC<QuickNotePopupProps> = ({ actionCount, popupText }) => {
    const anchor = React.useRef(null);
    const [show, setShow] = React.useState(false);

    if (actionCount === 0) return null;
    return (
        <div ref={anchor}>
            <Popover anchor={anchor?.current} show={show}>
                {popupText}
            </Popover>
            : <Button className="p-0" type="button" onMouseLeave={() => setShow(false)} onMouseOver={() => setShow(true)} onClick={() => setShow(s => !s)} fillMode="flat" startIcon={<SvgIcon icon={svg.wrenchIcon}></SvgIcon>} >+{actionCount}</Button>
        </div>
    )
}

export default QuickNotesList;